import dayjs from 'dayjs';
import {
  LEAVE_KIND, branchById, currentContract, deptOf, fmtD, isPaused, isWorking, licenseState, positionById,
  type Employee, type HrState, type LeaveKind, type LeaveRecord, type Log,
} from './hrLogic';
import type { PayPeriod, PaySettings, Adjust } from './payLogic';

/**
 * Phân ca – chấm công – đơn từ. Dữ liệu nhân sự (danh sách người, HĐ, nghỉ phép) lấy từ hrStore;
 * ở đây chỉ lưu ca, lịch xếp ca, giờ vào/ra, đơn từ và trạng thái chốt công.
 */

/* ============================== Ca làm việc ============================== */

export interface ShiftDef {
  id: string; code: string; name: string;
  /** 'HH:mm' — giờ kết thúc ≤ giờ bắt đầu nghĩa là ca qua đêm */
  start: string; end: string;
  breakMin: number;
  /** Công quy đổi của ca (ca 8 tiếng = 1 công) */
  work: number;
  color: string;
  /** Rỗng = dùng ở mọi chi nhánh */
  branchIds: string[];
  /** Số người tối thiểu mỗi ca ở mỗi chi nhánh */
  minStaff?: number;
  /** Ca có khám / làm thủ thuật: phải có bác sĩ có GPHN còn hạn */
  needDoctor?: boolean;
  active: boolean;
}

export const SHIFT_COLORS = [
  'bg-sky-100 text-sky-800 border-sky-200', 'bg-amber-100 text-amber-800 border-amber-200', 'bg-violet-100 text-violet-800 border-violet-200',
  'bg-emerald-100 text-emerald-800 border-emerald-200', 'bg-rose-100 text-rose-800 border-rose-200', 'bg-slate-200 text-slate-800 border-slate-300',
  'bg-indigo-100 text-indigo-800 border-indigo-200', 'bg-teal-100 text-teal-800 border-teal-200',
];

export const toMin = (hhmm: string) => { const [h, m] = hhmm.split(':').map(Number); return (h || 0) * 60 + (m || 0); };
export const fromMin = (m: number) => `${String(Math.floor(((m % 1440) + 1440) % 1440 / 60)).padStart(2, '0')}:${String(((m % 60) + 60) % 60).padStart(2, '0')}`;
/** Khoảng phút tính từ 00:00 của ngày bắt đầu ca */
export function shiftSpan(sh: Pick<ShiftDef, 'start' | 'end'>) {
  const a = toMin(sh.start);
  let b = toMin(sh.end);
  if (b <= a) b += 1440;
  return { a, b };
}
export const isOvernight = (sh: Pick<ShiftDef, 'start' | 'end'>) => toMin(sh.end) <= toMin(sh.start);
export const shiftHours = (sh: Pick<ShiftDef, 'start' | 'end' | 'breakMin'>) => { const { a, b } = shiftSpan(sh); return Math.max(0, (b - a - sh.breakMin) / 60); };
/** Số phút nằm trong khung đêm 22:00 – 06:00 */
export function nightMinutes(a: number, b: number) {
  const ov = (x: number, y: number) => Math.max(0, Math.min(b, y) - Math.max(a, x));
  return ov(0, 360) + ov(1320, 1800) + ov(2760, 3240);
}
export const shiftNightHours = (sh: ShiftDef) => { const { a, b } = shiftSpan(sh); return nightMinutes(a, b) / 60; };
export const shiftLabel = (sh: ShiftDef) => `${sh.start}–${sh.end}`;
export const shiftAt = (date: string, sh: Pick<ShiftDef, 'start' | 'end'>) => {
  const { a, b } = shiftSpan(sh);
  const d0 = dayjs(date).startOf('day');
  return { startAt: d0.add(a, 'minute').valueOf(), endAt: d0.add(b, 'minute').valueOf() };
};

export function validateShift(list: ShiftDef[], d: Partial<ShiftDef>): string[] {
  const errors: string[] = [];
  if (!d.code?.trim()) errors.push('Nhập mã ca');
  else if (list.some(x => x.id !== d.id && x.code.trim().toLowerCase() === d.code!.trim().toLowerCase())) errors.push(`Mã ca ${d.code} đã có`);
  if (!d.name?.trim()) errors.push('Nhập tên ca');
  if (!/^\d{2}:\d{2}$/.test(d.start || '') || !/^\d{2}:\d{2}$/.test(d.end || '')) errors.push('Chọn giờ bắt đầu và kết thúc');
  else {
    const h = shiftHours({ start: d.start!, end: d.end!, breakMin: d.breakMin || 0 });
    if (h <= 0) errors.push('Thời gian nghỉ giữa ca dài hơn ca làm');
    if (h > 12) errors.push('Ca dài quá 12 giờ — tách thành 2 ca');
  }
  if (!(d.work! > 0) || d.work! > 1.5) errors.push('Công quy đổi từ 0,1 tới 1,5');
  return errors;
}

/* ============================== Xếp ca & chấm công ============================== */

export type PunchSrc = 'device' | 'app' | 'import' | 'manual';
export const PUNCH_SRC: Record<PunchSrc, string> = { device: 'Máy chấm công', app: 'App / GPS', import: 'Nhập file', manual: 'Giải trình được duyệt' };

/** Một ca đã xếp cho một người; giờ vào/ra ghi thẳng trên ca */
export interface Assign {
  id: string; empId: string; date: string; shiftId: string; branchId: string;
  inAt?: number; outAt?: number; src?: PunchSrc;
  by?: string; at?: number; note?: string;
}

export type AssignState = 'future' | 'ongoing' | 'ok' | 'late' | 'early' | 'late_early' | 'forgot_in' | 'forgot_out' | 'absent' | 'leave';
export const ASSIGN_STATE: Record<AssignState, { label: string; badge: string }> = {
  future: { label: 'Chưa tới ca', badge: 'bg-gray-50 text-gray-500 border-gray-200' },
  ongoing: { label: 'Đang trong ca', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  ok: { label: 'Đúng giờ', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  late: { label: 'Đi muộn', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  early: { label: 'Về sớm', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  late_early: { label: 'Muộn & về sớm', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  forgot_in: { label: 'Quên chấm vào', badge: 'bg-orange-50 text-orange-700 border-orange-200' },
  forgot_out: { label: 'Quên chấm ra', badge: 'bg-orange-50 text-orange-700 border-orange-200' },
  absent: { label: 'Vắng', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  leave: { label: 'Nghỉ có đơn', badge: 'bg-violet-50 text-violet-700 border-violet-200' },
};

export interface AssignEval { a: Assign; sh: ShiftDef; state: AssignState; lateMin: number; earlyMin: number; hours: number; nightH: number; work: number }

/** Chưa chấm ra sau khi hết ca bao lâu thì coi là quên chấm */
const OUT_WINDOW_MIN = 240;

export function evalAssign(a: Assign, sh: ShiftDef, o: { now: number; grace: number; leave?: boolean }): AssignEval {
  const base = { a, sh, lateMin: 0, earlyMin: 0, hours: 0, nightH: 0, work: 0 };
  if (o.leave) return { ...base, state: 'leave' };
  const { startAt, endAt } = shiftAt(a.date, sh);
  if (!a.inAt && !a.outAt) return { ...base, state: o.now < startAt ? 'future' : o.now < endAt ? 'ongoing' : 'absent' };
  if (a.inAt && !a.outAt) return { ...base, state: o.now < endAt + OUT_WINDOW_MIN * 60000 ? 'ongoing' : 'forgot_out' };
  if (!a.inAt) return { ...base, state: 'forgot_in' };
  const rawLate = Math.round((a.inAt - startAt) / 60000);
  const lateMin = rawLate > o.grace ? rawLate : 0;
  const earlyMin = Math.max(0, Math.round((endAt - a.outAt!) / 60000));
  const hours = Math.max(0, shiftHours(sh) - (lateMin + earlyMin) / 60);
  const state: AssignState = lateMin && earlyMin ? 'late_early' : lateMin ? 'late' : earlyMin ? 'early' : 'ok';
  return { ...base, state, lateMin, earlyMin, hours, nightH: shiftNightHours(sh), work: sh.work };
}

/* ============================== Đơn từ ============================== */

export type ReqKind = 'leave' | 'explain' | 'ot' | 'swap';
export const REQ_KIND: Record<ReqKind, { label: string; badge: string }> = {
  leave: { label: 'Xin nghỉ', badge: 'bg-violet-50 text-violet-700 border-violet-200' },
  explain: { label: 'Giải trình chấm công', badge: 'bg-orange-50 text-orange-700 border-orange-200' },
  ot: { label: 'Làm thêm giờ', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  swap: { label: 'Đổi ca', badge: 'bg-teal-50 text-teal-700 border-teal-200' },
};
export type ReqState = 'pending' | 'approved' | 'rejected' | 'cancelled';
export const REQ_STATE: Record<ReqState, { label: string; badge: string }> = {
  pending: { label: 'Chờ duyệt', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Đã duyệt', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Từ chối', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  cancelled: { label: 'Đã hủy', badge: 'bg-gray-50 text-gray-500 border-gray-200' },
};

export interface TimeReq {
  id: string; code: string; empId: string; kind: ReqKind; status: ReqState;
  /* Xin nghỉ */
  leaveKind?: LeaveKind; from?: string; to?: string; days?: number;
  /* Giải trình / làm thêm / đổi ca: ngày áp dụng */
  date?: string;
  /* Giải trình: ca và giờ vào / ra thực tế */
  assignId?: string; inTime?: string; outTime?: string;
  /* Làm thêm giờ */
  otFrom?: string; otTo?: string; hours?: number; nightHours?: number;
  /* Đổi ca: người nhận ca */
  toEmpId?: string;
  reason: string;
  by: string; at: number;
  decidedBy?: string; decidedAt?: number; decideNote?: string;
  /** Bản ghi nghỉ đã ghi vào hồ sơ nhân sự khi duyệt */
  leaveId?: string;
}

export const reqDate = (r: TimeReq) => r.date || r.from || '';
export function reqSummary(r: TimeReq, shiftName?: string) {
  if (r.kind === 'leave') return `${LEAVE_KIND[r.leaveKind || 'annual'].label} · ${fmtD(r.from)}${r.to !== r.from ? ` – ${fmtD(r.to)}` : ''} · ${r.days} ngày`;
  if (r.kind === 'explain') return `${fmtD(r.date)}${shiftName ? ` · ${shiftName}` : ''} · vào ${r.inTime || '—'} / ra ${r.outTime || '—'}`;
  if (r.kind === 'ot') return `${fmtD(r.date)} · ${r.otFrom}–${r.otTo} · ${r.hours} giờ${r.nightHours ? ` (đêm ${r.nightHours})` : ''}`;
  return `${fmtD(r.date)}${shiftName ? ` · ${shiftName}` : ''}`;
}

/** Giờ làm thêm từ khoảng giờ (qua đêm nếu giờ kết thúc ≤ giờ bắt đầu) */
export function otHours(from: string, to: string) {
  const { a, b } = shiftSpan({ start: from, end: to });
  return { hours: Math.round(((b - a) / 60) * 100) / 100, night: Math.round((nightMinutes(a, b) / 60) * 100) / 100 };
}

/* ============================== Thiết lập chấm công ============================== */

export interface Holiday { date: string; name: string }
export interface TimeSettings {
  /** Đi muộn trong số phút này không tính */
  graceMin: number;
  /** Ngày nghỉ hằng tuần (0 = Chủ nhật) — dùng tính công chuẩn */
  weeklyOff: number[];
  holidays: Holiday[];
  /** Trần giờ làm thêm (Điều 107 BLLĐ 2019) */
  otMonthCap: number; otYearCap: number;
}

/** Ngày lễ năm 2026 — lịch nghỉ Tết / bù cụ thể theo thông báo hằng năm, sửa tại Thiết lập */
export const DEFAULT_HOLIDAYS: Holiday[] = [
  { date: '2026-01-01', name: 'Tết Dương lịch' },
  { date: '2026-02-16', name: 'Tết Nguyên đán' }, { date: '2026-02-17', name: 'Tết Nguyên đán' }, { date: '2026-02-18', name: 'Tết Nguyên đán' },
  { date: '2026-02-19', name: 'Tết Nguyên đán' }, { date: '2026-02-20', name: 'Tết Nguyên đán' },
  { date: '2026-04-27', name: 'Giỗ Tổ Hùng Vương (nghỉ bù)' },
  { date: '2026-04-30', name: 'Ngày Chiến thắng' }, { date: '2026-05-01', name: 'Quốc tế Lao động' },
  { date: '2026-09-01', name: 'Quốc khánh' }, { date: '2026-09-02', name: 'Quốc khánh' },
];

export const DEFAULT_TIME_SETTINGS: TimeSettings = { graceMin: 5, weeklyOff: [0], holidays: DEFAULT_HOLIDAYS, otMonthCap: 40, otYearCap: 200 };

/* ============================== State ============================== */

export interface TimeLock { month: string; branchId: string; at: number; by: string }
export interface Publish { branchId: string; week: string; at: number; by: string }

export interface TpState {
  v: 1; seededAt: number; demoVersion?: number;
  /** seededAt của dữ liệu nhân sự lúc dựng demo — đổi thì dựng lại */
  hrSeededAt?: number;
  shifts: ShiftDef[];
  assigns: Assign[];
  requests: TimeReq[];
  locks: TimeLock[];
  published: Publish[];
  time: TimeSettings;
  pay: PaySettings;
  periods: PayPeriod[];
  adjustments: Adjust[];
  log: Log[];
}

/* ============================== Tiện ích ngày ============================== */

export const monthOf = (d: string) => d.slice(0, 7);
export const monthDays = (month: string) => {
  const d0 = dayjs(`${month}-01`);
  return Array.from({ length: d0.daysInMonth() }, (_, i) => d0.add(i, 'day').format('YYYY-MM-DD'));
};
export const monthEnd = (month: string) => dayjs(`${month}-01`).endOf('month').format('YYYY-MM-DD');
export const fmtMonth = (month: string) => dayjs(`${month}-01`).format('MM/YYYY');
export const weekStart = (d: string) => { const x = dayjs(d); return x.subtract((x.day() + 6) % 7, 'day').format('YYYY-MM-DD'); };
export const weekDays = (start: string) => Array.from({ length: 7 }, (_, i) => dayjs(start).add(i, 'day').format('YYYY-MM-DD'));
export const WD = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
export const holidayOf = (t: TimeSettings, d: string) => t.holidays.find(h => h.date === d);
/** Công chuẩn của tháng: số ngày trừ ngày nghỉ hằng tuần (ngày lễ vẫn được hưởng lương nên vẫn tính) */
export const stdDays = (t: TimeSettings, month: string) => monthDays(month).filter(d => !t.weeklyOff.includes(dayjs(d).day())).length;

export const shiftById = (t: TpState, id?: string) => t.shifts.find(x => x.id === id);
export const isLocked = (t: TpState, month: string, branchId: string) => t.locks.some(l => l.month === month && l.branchId === branchId);
export const empAssigns = (t: TpState, empId: string, from: string, to: string) => t.assigns.filter(a => a.empId === empId && a.date >= from && a.date <= to);
/** Nhân sự thuộc tháng: đi làm ít nhất 1 ngày trong tháng */
export const inMonth = (e: Employee, month: string) => e.hireDate <= monthEnd(month) && (!e.leftDate || e.leftDate >= `${month}-01`);

/** Đợt nghỉ phủ ngày d và phần ngày nghỉ của ngày đó (đợt nghỉ nửa ngày → 0,5) */
export function leaveOn(e: Employee, d: string): { r: LeaveRecord; part: number } | undefined {
  const r = (e.leaveRecords || []).find(x => x.from <= d && d <= x.to);
  if (!r) return undefined;
  let n = 0;
  for (let x = dayjs(r.from); !x.isAfter(dayjs(r.to), 'day'); x = x.add(1, 'day')) if (x.day() !== 0) n += 1;
  return { r, part: n ? Math.min(1, r.days / n) : 1 };
}
const PAID_LEAVE: LeaveKind[] = ['annual', 'personal'];
export const LEAVE_CODE: Record<LeaveKind, string> = { annual: 'P', personal: 'R', sick: 'Ô', unpaid: 'KL', maternity: 'TS' };

/* ============================== Kiểm tra xếp ca ============================== */

export interface AssignDraft { empId: string; date: string; shiftId: string; branchId: string; id?: string }

export function validateAssign(s: HrState, t: TpState, d: AssignDraft, today: string): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  const e = s.employees.find(x => x.id === d.empId);
  const sh = shiftById(t, d.shiftId);
  if (!e) return { errors: ['Không tìm thấy nhân sự'], warnings };
  if (!sh || !sh.active) return { errors: ['Ca không còn sử dụng'], warnings };
  if (sh.branchIds.length && !sh.branchIds.includes(d.branchId)) errors.push(`${sh.name} không áp dụng ở ${branchById(s, d.branchId)?.short}`);
  if (d.date < e.hireDate) errors.push(`${e.name} vào làm từ ${fmtD(e.hireDate)}`);
  if (e.leftDate && d.date > e.leftDate) errors.push(`${e.name} đã nghỉ việc từ ${fmtD(e.leftDate)}`);
  if (isPaused(e, d.date)) errors.push(`${e.name} đang tạm hoãn HĐ`);
  const lv = leaveOn(e, d.date);
  if (lv && lv.part >= 1) errors.push(`${e.name} nghỉ ${LEAVE_KIND[lv.r.kind].label.toLowerCase()} ${fmtD(lv.r.from)} – ${fmtD(lv.r.to)}`);
  if (isLocked(t, monthOf(d.date), e.branchId)) errors.push(`Tháng ${fmtMonth(monthOf(d.date))} đã chốt công`);
  const c = currentContract(s, e.id, d.date);
  if (!c && isWorking(e, d.date)) warnings.push('Chưa có hợp đồng / thỏa thuận thử việc');
  const lic = licenseState(s, e, d.date);
  if (lic.need === 'gphn' && (lic.state === 'expired' || lic.state === 'missing')) errors.push(`Không được xếp ca hành nghề: GPHN ${lic.state === 'expired' ? 'đã hết hạn' : 'chưa cập nhật'}`);
  else if (lic.state === 'expired' || lic.state === 'missing') warnings.push(`Chứng chỉ chuyên môn ${lic.state === 'expired' ? 'đã hết hạn' : 'chưa cập nhật'}`);

  // Trùng giờ với ca khác (kể cả ca qua đêm hôm trước)
  const me = shiftAt(d.date, sh);
  const near = t.assigns.filter(a => a.empId === e.id && a.id !== d.id && Math.abs(dayjs(a.date).diff(dayjs(d.date), 'day')) <= 1);
  near.forEach(a => {
    const o = shiftById(t, a.shiftId);
    if (!o) return;
    const x = shiftAt(a.date, o);
    if (x.startAt < me.endAt && me.startAt < x.endAt) errors.push(`Trùng giờ với ${o.name} ${fmtD(a.date)} (${shiftLabel(o)})`);
    else if (a.date !== d.date) {
      const gap = (x.startAt >= me.endAt ? x.startAt - me.endAt : me.startAt - x.endAt) / 3600000;
      if (gap < 12) warnings.push(`Chỉ nghỉ ${Math.round(gap)} giờ giữa 2 ca (${fmtD(a.date)}) — luật yêu cầu tối thiểu 12 giờ khi chuyển ca`);
    }
  });
  const sameDay = near.filter(a => a.date === d.date).reduce((h, a) => h + shiftHours(shiftById(t, a.shiftId) || sh), 0) + shiftHours(sh);
  if (sameDay > 10) warnings.push(`Tổng ${sameDay} giờ trong ngày — vượt 10 giờ làm việc bình thường`);
  // Ngày nghỉ hằng tuần: không làm 7 ngày liên tục
  let run = 1;
  for (let i = 1; i <= 6; i += 1) { if (t.assigns.some(a => a.empId === e.id && a.date === dayjs(d.date).subtract(i, 'day').format('YYYY-MM-DD'))) run += 1; else break; }
  for (let i = 1; i <= 6; i += 1) { if (t.assigns.some(a => a.empId === e.id && a.date === dayjs(d.date).add(i, 'day').format('YYYY-MM-DD'))) run += 1; else break; }
  if (run >= 7) warnings.push(`Làm ${run} ngày liên tục, chưa có ngày nghỉ hằng tuần`);
  if (d.branchId !== e.branchId) warnings.push(`Làm tăng cường tại ${branchById(s, d.branchId)?.short}`);
  if (lv && lv.part < 1) warnings.push(`Có nghỉ ${lv.part} ngày (${LEAVE_KIND[lv.r.kind].label.toLowerCase()})`);
  const pend = t.requests.find(r => r.empId === e.id && r.status === 'pending' && r.kind === 'leave' && r.from! <= d.date && d.date <= r.to!);
  if (pend) warnings.push(`Đang có đơn xin nghỉ chờ duyệt ${fmtD(pend.from)} – ${fmtD(pend.to)}`);
  if (d.date < today) warnings.push('Xếp ca cho ngày đã qua — cần có giờ vào/ra hoặc giải trình');
  return { errors, warnings };
}

/** Cảnh báo định mức từng ca của một chi nhánh trong ngày */
export interface CoverIssue { date: string; branchId: string; shiftId: string; level: 'crit' | 'warn'; text: string }
export function coverage(s: HrState, t: TpState, branchId: string, dates: string[]): CoverIssue[] {
  const br = branchById(s, branchId);
  if (!br?.clinic) return [];
  const out: CoverIssue[] = [];
  const shifts = t.shifts.filter(x => x.active && (!x.branchIds.length || x.branchIds.includes(branchId)) && (x.minStaff || x.needDoctor));
  dates.forEach(date => {
    if (holidayOf(t.time, date)) return;
    shifts.forEach(sh => {
      const list = t.assigns.filter(a => a.branchId === branchId && a.date === date && a.shiftId === sh.id)
        .map(a => s.employees.find(e => e.id === a.empId)).filter((e): e is Employee => !!e && !(leaveOn(e, date)?.part === 1));
      if (sh.minStaff && list.length < sh.minStaff) out.push({ date, branchId, shiftId: sh.id, level: list.length ? 'warn' : 'crit', text: `${sh.name}: ${list.length}/${sh.minStaff} người` });
      if (sh.needDoctor && list.length && !list.some(e => deptOf(s, e) === 'doctor' && ['ok', 'expiring'].includes(licenseState(s, e, date).state))) {
        out.push({ date, branchId, shiftId: sh.id, level: 'crit', text: `${sh.name}: không có bác sĩ có GPHN còn hạn` });
      }
    });
  });
  return out;
}

/* ============================== Bảng công tháng ============================== */

export interface TsDay {
  date: string;
  /** Ký hiệu trên bảng công */
  code: string;
  tone: string;
  title: string;
  /** Công làm thực tế */
  work: number;
  /** Ngày hưởng lương (công + phép có lương + lễ) */
  paid: number;
  evals: AssignEval[];
}
export interface TsRow {
  e: Employee;
  days: TsDay[];
  std: number;
  work: number; paidLeave: number; holiday: number; unpaid: number; sick: number; maternity: number;
  /** Số ngày hưởng lương = công + phép có lương + lễ */
  paidDays: number;
  absent: number; forgot: number; lateCount: number; lateMin: number; earlyMin: number;
  nightH: number;
  /** Giờ làm vào ngày lễ (trả 300%) */
  holidayWorkH: number;
  ot: { normal: number; off: number; holiday: number; night: number; total: number };
  /** Ngày nghỉ không hưởng lương / không đi làm (để xét đóng BHXH) */
  offWork: number;
  pending: number;
  issues: string[];
}

export function otDayType(t: TimeSettings, d: string): 'normal' | 'off' | 'holiday' {
  if (holidayOf(t, d)) return 'holiday';
  return t.weeklyOff.includes(dayjs(d).day()) ? 'off' : 'normal';
}

export function timesheetRow(s: HrState, t: TpState, e: Employee, month: string, now = Date.now()): TsRow {
  const std = stdDays(t.time, month);
  const today = dayjs(now).format('YYYY-MM-DD');
  const row: TsRow = {
    e, days: [], std, work: 0, paidLeave: 0, holiday: 0, unpaid: 0, sick: 0, maternity: 0, paidDays: 0,
    absent: 0, forgot: 0, lateCount: 0, lateMin: 0, earlyMin: 0, nightH: 0, holidayWorkH: 0,
    ot: { normal: 0, off: 0, holiday: 0, night: 0, total: 0 }, offWork: 0, pending: 0, issues: [],
  };
  const list = t.assigns.filter(a => a.empId === e.id && monthOf(a.date) === month);
  monthDays(month).forEach(date => {
    const day: TsDay = { date, code: '', tone: '', title: '', work: 0, paid: 0, evals: [] };
    const weeklyOff = t.time.weeklyOff.includes(dayjs(date).day());
    if (!isWorking(e, date) || isPaused(e, date)) {
      day.code = ''; day.tone = 'bg-gray-50 dark:bg-gray-800/40'; day.title = date < e.hireDate ? 'Chưa vào làm' : isPaused(e, date) ? 'Tạm hoãn HĐ' : 'Đã nghỉ việc';
      if (isPaused(e, date) && !weeklyOff) row.offWork += 1;
      row.days.push(day);
      return;
    }
    const lv = leaveOn(e, date);
    const hol = holidayOf(t.time, date);
    const evs = list.filter(a => a.date === date).map(a => {
      const sh = shiftById(t, a.shiftId);
      return sh ? evalAssign(a, sh, { now, grace: t.time.graceMin, leave: !!lv && lv.part >= 1 }) : null;
    }).filter((x): x is AssignEval => !!x);
    day.evals = evs;
    const worked = evs.reduce((n, x) => n + x.work, 0);
    if (hol && !weeklyOff) {
      row.holiday += 1; day.paid = 1;
      const h = evs.reduce((n, x) => n + x.hours, 0);
      row.holidayWorkH += h;
      day.code = h ? 'L+' : 'L'; day.tone = 'bg-rose-50 text-rose-700'; day.title = `${hol.name}${h ? ` · làm ${h} giờ (300%)` : ''}`;
      row.days.push(day);
      return;
    }
    if (lv) {
      const k = lv.r.kind;
      if (PAID_LEAVE.includes(k)) { row.paidLeave += lv.part; day.paid += lv.part; }
      else if (k === 'unpaid') row.unpaid += lv.part;
      else if (k === 'sick') row.sick += lv.part;
      else row.maternity += lv.part;
      if (!PAID_LEAVE.includes(k) && !weeklyOff) row.offWork += lv.part;
      day.code = lv.part < 1 ? `${LEAVE_CODE[k]}½` : LEAVE_CODE[k];
      day.tone = PAID_LEAVE.includes(k) ? 'bg-violet-50 text-violet-700' : 'bg-gray-100 text-gray-600';
      day.title = LEAVE_KIND[k].label;
    }
    if (evs.length) {
      day.work = worked; day.paid += worked;
      row.work += worked;
      evs.forEach(x => {
        row.lateMin += x.lateMin; row.earlyMin += x.earlyMin; row.nightH += x.work ? x.nightH : 0;
        if (x.lateMin) row.lateCount += 1;
        if (x.state === 'absent') row.absent += 1;
        if (x.state === 'forgot_in' || x.state === 'forgot_out') row.forgot += 1;
      });
      if (!lv || lv.part < 1) {
        const bad = evs.find(x => x.state === 'absent') || evs.find(x => x.state === 'forgot_in' || x.state === 'forgot_out');
        const late = evs.some(x => x.lateMin || x.earlyMin);
        const future = evs.every(x => x.state === 'future' || x.state === 'ongoing');
        if (bad) { day.code = bad.state === 'absent' ? 'V' : 'Q'; day.tone = bad.state === 'absent' ? 'bg-rose-100 text-rose-700' : 'bg-orange-100 text-orange-700'; }
        else if (future) { day.code = evs.map(x => x.sh.code).join('+'); day.tone = 'text-gray-400'; }
        else { day.code = lv ? `${day.code}+${worked}` : worked >= 1 ? 'x' : String(worked).replace('.', ','); day.tone = late ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'; }
        day.title = evs.map(x => `${x.sh.name} ${shiftLabel(x.sh)}: ${ASSIGN_STATE[x.state].label}${x.a.inAt ? ` · vào ${dayjs(x.a.inAt).format('HH:mm')}` : ''}${x.a.outAt ? ` · ra ${dayjs(x.a.outAt).format('HH:mm')}` : ''}${x.lateMin ? ` · muộn ${x.lateMin}′` : ''}${x.earlyMin ? ` · sớm ${x.earlyMin}′` : ''}`).join('\n');
      }
      if (evs.some(x => x.state === 'absent') && !weeklyOff) row.offWork += 1;
    } else if (!lv) {
      day.code = weeklyOff ? '' : '·'; day.tone = weeklyOff ? 'bg-gray-50 dark:bg-gray-800/40' : ''; day.title = weeklyOff ? 'Nghỉ hằng tuần' : date > today ? 'Chưa xếp ca' : 'Không có ca';
    }
    row.days.push(day);
  });
  // Làm thêm giờ đã duyệt
  t.requests.filter(r => r.empId === e.id && r.kind === 'ot' && r.status === 'approved' && r.date && monthOf(r.date) === month).forEach(r => {
    const k = otDayType(t.time, r.date!);
    row.ot[k] += r.hours || 0; row.ot.night += r.nightHours || 0; row.ot.total += r.hours || 0;
  });
  row.pending = t.requests.filter(r => r.empId === e.id && r.status === 'pending' && monthOf(reqDate(r)) === month).length;
  row.paidDays = Math.round((row.work + row.paidLeave + row.holiday) * 100) / 100;
  if (row.forgot) row.issues.push(`${row.forgot} lần quên chấm chưa giải trình`);
  if (row.absent) row.issues.push(`${row.absent} ca vắng không có đơn`);
  if (row.pending) row.issues.push(`${row.pending} đơn chờ duyệt`);
  if (row.work + row.paidLeave + row.holiday > std + 0.01) row.issues.push(`Vượt công chuẩn ${Math.round((row.paidDays - std) * 10) / 10} công — cần đơn làm thêm`);
  return row;
}

/** Nhân sự thuộc bảng công của chi nhánh trong tháng (theo chi nhánh đang làm) */
export const sheetEmployees = (s: HrState, month: string, branchId?: string) =>
  s.employees.filter(e => inMonth(e, month) && (!branchId || e.branchId === branchId))
    .sort((a, b) => a.branchId.localeCompare(b.branchId) || (positionById(s, a.positionId)?.deptId || '').localeCompare(positionById(s, b.positionId)?.deptId || '') || a.code.localeCompare(b.code));

/** Điều kiện chốt công một chi nhánh */
export function lockCheck(s: HrState, t: TpState, month: string, branchId: string, today: string): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  if (isLocked(t, month, branchId)) errors.push('Đã chốt công');
  if (monthEnd(month) >= today) errors.push(`Tháng ${fmtMonth(month)} chưa kết thúc`);
  const emps = sheetEmployees(s, month, branchId);
  const ids = new Set(emps.map(e => e.id));
  const pend = t.requests.filter(r => ids.has(r.empId) && r.status === 'pending' && monthOf(reqDate(r)) === month);
  if (pend.length) errors.push(`Còn ${pend.length} đơn chờ duyệt trong tháng`);
  let forgot = 0; let absent = 0;
  emps.forEach(e => { const r = timesheetRow(s, t, e, month); forgot += r.forgot; absent += r.absent; });
  if (forgot) warnings.push(`${forgot} lần quên chấm chưa giải trình — chốt thì các ca này không được tính công`);
  if (absent) warnings.push(`${absent} ca vắng không có đơn — tính là nghỉ không lương`);
  return { errors, warnings };
}
